import React, { useState, useEffect } from 'react';
import { Loader2, FileText, RefreshCcw, Languages, MessageCircle,LucideMails } from 'lucide-react';
import AppShell from '../../components/AppShell';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_API_BASE;

export default function MailMentorApp() {
  const [emailText, setEmailText] = useState('');
  const { t } = useTranslation();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedReply, setSelectedReply] = useState(null);

  useEffect(() => {
    document.title = t("Email Mentor") + " – Ennoverse";
  }, []);

  const handleAnalyze = async () => {
    if (!emailText.trim()) return;
    setLoading(true);
    setAnalysis(null);

    try {
      const res = await fetch(`${API}/api/email/assist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ emailText }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to analyze email');

      let replies = [];
      if (Array.isArray(data.replies)) {
        replies = data.replies.map((r) => ({ label: '', text: typeof r === 'string' ? r : r.content || JSON.stringify(r) }));
      } else if (typeof data.replies === 'object') {
        replies = Object.entries(data.replies).map(([label, value]) => {
          let raw = typeof value === 'string' ? value : value?.content || JSON.stringify(value);
          let parsed;
          try {
            parsed = JSON.parse(raw);
            const actualLabel = Object.keys(parsed)[0];
            const actualText = parsed[actualLabel];
            return { label: actualLabel, text: actualText };
          } catch {
            return { label, text: raw };
          }
        });
      }

      setAnalysis({
        summary: data.summary,
        urgency: data.urgency,
        tone: data.tone,
        replies,
      });
    } catch (err) {
      console.error('Analyze error:', err);
      toast.error(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Reply copied to clipboard');
  };

  return (
    <AppShell>
      <div className="col-span-12">
      <div className="text-center space-y-1">
      <h1 className="text-2xl font-semibold flex items-center justify-center gap-2">
      <LucideMails className="w-5 h-5 text-primary" /> {t('Email Mentor')}
        </h1>
        <p className="text-gray-500 text-sm">{t('emailMentorDescription')}</p>
      </div>
      <div className="space-y-4">
        <Textarea
          value={emailText}
          onChange={(e) => setEmailText(e.target.value)}
          placeholder={t('pasteConversation')}
          rows={10}
          className="mb-4"
        />

        <div className="flex flex-wrap items-center gap-4 mb-6">
          <Button onClick={handleAnalyze} disabled={loading || !emailText.trim()}>
            {loading ? 'Analyzing...' : t('analyzeEmail')}
          </Button>

          {analysis && (
            <div className="flex gap-4">
              <Card className="w-40">
                <CardContent className="py-2 px-3">
                  <h4 className="text-sm font-semibold">🔥 {t('urgency')}</h4>
                  <p className="text-sm text-gray-600">{analysis.urgency}</p>
                </CardContent>
              </Card>
              <Card className="w-40">
                <CardContent className="py-2 px-3">
                  <h4 className="text-sm font-semibold">🗣️ {t('tone')}</h4>
                  <p className="text-sm text-gray-600">{analysis.tone}</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {analysis && (
          <div className="space-y-6">
            <Card>
              <CardContent className="space-y-2 py-4">
                <h3 className="text-lg font-semibold">🧠 {t('tone')}</h3>
                <p>{analysis.summary}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-4 py-4">
                <h3 className="text-lg font-semibold">✍️ {t('suggestedreplies')}</h3>
                <div className="max-h-[60vh] overflow-y-auto space-y-4">
                  {analysis.replies.map((item, index) => (
                    <Card key={index} className="bg-gray-50">
                      <CardContent className="p-3 relative">
                      <div className="flex justify-between items-center mb-2">
                      {item.label && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                              {item.label[0]?.toUpperCase() + item.label.slice(1)}
                            </span>
                          )}
                          <button
                            onClick={() => handleCopy(item.text)}
                            className="text-gray-400 hover:text-blue-600"
                            title="Copy Reply"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{item.text}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </div>
    </AppShell>
  );
}